import * as Sentry from '@sentry/react';

const logLevels = ['silent', 'debug', 'info', 'warn', 'error'] as const;

type LogLevel = typeof logLevels[number];

type LogLevelFunction = Exclude<LogLevel, 'silent'> | 'log';

interface LogLevelInfo {
  priority: number,
  func?: LogLevelFunction,
  aliases?: LogLevelFunction[],
  sentryLevel?: Sentry.SeverityLevel,
}

type LogLevels = Record<LogLevel, LogLevelInfo>;

type LogFunction = (message?: unknown, ...optionalParams: unknown[]) => void;

interface LoggerInterface {
  debug: LogFunction,
  info: LogFunction,
  log: LogFunction,
  warn: LogFunction,
  error: LogFunction,
}

// Log level configuration
const logLevelsData: LogLevels = {
  debug: {
    priority: 1,
    func: 'debug',
    sentryLevel: 'debug',
  },
  info: {
    priority: 2,
    func: 'info',
    aliases: ['log'],
    sentryLevel: 'info',
  },
  warn: {
    priority: 3,
    func: 'warn',
    sentryLevel: 'warning',
  },
  error: {
    priority: 4,
    func: 'error',
    sentryLevel: 'error',
  },
  silent: {
    priority: 100,
  },
};

const getLogLevelInfo = (level: LogLevel) => (
  logLevelsData[level]
);

// nullLogger used to swallow output when logLevel config is set to a higher level than what's being logged
const nullLogger: LogFunction = () => {};

// sentryLogger used to pass logs to sentry
const sentryLogger = (level: Sentry.SeverityLevel, message: unknown) => {
  if (level === 'error' && message instanceof Error) {
    Sentry.captureException(message);
    return;
  }

  if (['error', 'warn'].includes(level) && typeof message === 'string') {
    Sentry.captureMessage(message, level);
    return;
  }

  if (typeof message === 'string') {
    Sentry.addBreadcrumb({
      category: 'log',
      message,
      level,
    });
    return;
  }

  Sentry.addBreadcrumb({
    category: 'log',
    message: 'data',
    data: message as { string: unknown },
    level,
  });
};

// consoleLogger ...
const consoleLogger = (func: LogLevelFunction) => (
  console[func].bind(console)
);

class Logger implements LoggerInterface {
  debug: LogFunction = nullLogger;

  info: LogFunction = nullLogger;

  log: LogFunction = nullLogger; // alias of "info" logger

  warn: LogFunction = nullLogger;

  error: LogFunction = nullLogger;

  // Setup logger functions based on config
  constructor(options?: { logLevel?: LogLevel, useSentry?: boolean }) {
    const { logLevel = 'silent', useSentry = false } = options ?? {};

    // Determine log level priority level (higher = more critical) based on config
    const { priority: logPriority } = getLogLevelInfo(logLevel);

    // Loop over all the log levels and setup the relevant logger functions
    logLevels.forEach((level) => {
      const {
        priority,
        func,
        aliases,
        sentryLevel,
      } = getLogLevelInfo(level);

      // Skip "silent" level
      if (!func) {
        return;
      }

      // Skip "debug"/"info"/"log" logs if our logLevel is "warn"/"error", etc
      if (logPriority > priority) {
        return;
      }

      // Attach the Sentry logger if sentry is available
      if (useSentry) {
        if (!sentryLevel) {
          return;
        }

        this[func] = message => sentryLogger(sentryLevel, message);
        aliases?.forEach((alias) => {
          this[alias] = message => sentryLogger(sentryLevel, message);
        });

        return;
      }

      // Otherwise attach the console logger (typically used when developing locally)
      this[func] = consoleLogger(func);
      aliases?.forEach((alias) => {
        this[alias] = consoleLogger(alias);
      });
    });
  }
}

const logLevelEnv = import.meta.env.VITE_LOG_LEVEL ?? null;
const logLevel = logLevels.includes(logLevelEnv as LogLevel) ? (logLevelEnv as LogLevel) : 'info';
const useSentry = !!import.meta.env.VITE_SENTRY_DSN;

export const logger = new Logger({
  logLevel,
  useSentry,
});
